import { Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
  selector: '[integer]'
})
export class IntegerDirective {
  @Input('minvalue') minvalue = 0;

  private check(value: string): boolean {
    // Ensure the value contains only digits and matches the minvalue criteria
    const isValidNumber = /^[0-9]+$/.test(value);
    const isAboveMinValue = parseInt(value, 10) >= this.minvalue;
    return isValidNumber && isAboveMinValue;
  }

  private removeLeadingZeros(value: string): string {
    // Remove leading zeros while preserving "0" if the value is exactly "0"
    return value.replace(/^0+(?!$)/, '');
  }

  private run(oldValue: string) {
    setTimeout(() => {
      let currentValue: string = this.el.nativeElement.value;
      if (currentValue !== '') {
        // Remove leading zeros
        currentValue = this.removeLeadingZeros(currentValue);
        this.el.nativeElement.value = currentValue;

        // Revert to the old value if validation fails
        if (!this.check(currentValue)) {
          this.el.nativeElement.value = oldValue;
        }
      }
    });
  }

  constructor(private el: ElementRef) {}

  @HostListener('input', ['$event'])
  onInput() {
    this.run(this.el.nativeElement.value);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // Allow navigation keys and backspace
    if (
      ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'].includes(event.key) ||
      (event.key >= '0' && event.key <= '9') // Allow numeric keys
    ) {
      return;
    }
    event.preventDefault(); // Prevent other keys, including '.'
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData?.getData('text');
    if (clipboardData) {
      const sanitizedValue = this.removeLeadingZeros(clipboardData);
      if (!this.check(sanitizedValue)) {
        event.preventDefault();
      } else {
        setTimeout(() => {
          this.el.nativeElement.value = sanitizedValue;
        });
      }
    }
  }
}
